'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { Carousel } from '@eliadmin/core/src/components/ui/carousel';

import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { ButtonIcon } from '../ui/button-icon';
import { memo, useCallback, useMemo, useState } from 'react';
import { CarouselControls } from '../ui/carousel-controls';

const INTERVAL = 5000;
const HERO_LINK_FALLBACKS: Record<string, string> = {
  '/apple-macbook-air-13-m4-16gb-256gb':
    '/macbook-air-13-apple-m4-10c-cpu-8c-gpu-16gb-256gb-hviezdny-biely-sk',
};

export function Hero({ slides }: { slides: TDynamicData[] }) {
  const carouselContext = useCarouselContext();
  const [readyImage, setReadyImage] = useState<string | null>(null);
  const firstImage = slides?.[0]?.image?.url ?? '/images/placeholder.png';
  const onFirstImageSettled = useCallback(() => setReadyImage(firstImage), [firstImage]);
  const autoplayReady = readyImage === firstImage;

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  // Progress is a compositor animation, not 60 React renders / second of every slide.
  // Replacing its key restarts it only when the selected slide actually changes.
  const renderedSlides = useMemo(
    () =>
      (slides ?? []).map((slide, index) => (
        <Slide
          slide={slide}
          index={index}
          key={slide.slug}
          onImageSettled={index === 0 ? onFirstImageSettled : undefined}
        />
      )),
    [slides, onFirstImageSettled],
  );

  return (
    <section className={cn('custom-section', 'relative py-12')}>
      <style>{`
        @keyframes mediatip-hero-progress { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .mediatip-hero-progress { animation: mediatip-hero-progress ${INTERVAL}ms linear both; }
        @media (prefers-reduced-motion: reduce) { .mediatip-hero-progress { animation: none; transform: scaleX(1); } }
      `}</style>
      <div
        className={cn(
          'custom-container',
          'relative z-10 flex flex-col items-center justify-center gap-8 sm:gap-12 md:gap-16 lg:gap-[108px]',
        )}
      >
        <div className="invisible hidden h-1 w-full md:block" />
        <CarouselControls
          className={{
            main: 'absolute top-1/2 z-20 hidden w-full -translate-y-1/2 items-center justify-between lg:flex',
          }}
        />
        <div className="w-full" style={{ clipPath: 'inset(-100vw 0)' }}>
          <Carousel
            intervalLength={INTERVAL}
            // Do not replace the initial banner while its image is still downloading.
            // Manual swipe/arrows remain available; errors also release autoplay.
            enableInterval={autoplayReady}
            // Embla's loop with only two full-width slides inside `overflow-visible` containers
            // parks both slides outside the clip area after the first wrap — the hero went blank on
            // mobile and tablet. Loop only when there are enough slides; autoplay wraps otherwise.
            enableLoop={(slides?.length ?? 0) > 2}
            className={{
              container: 'w-full overflow-visible',
              slide: 'flex w-full items-center justify-center overflow-visible',
              wrapper: 'w-full overflow-visible',
              main: 'w-full overflow-visible',
            }}
            slides={renderedSlides}
          />
        </div>

        <div className="flex w-full items-center justify-center gap-1">
          {(slides ?? []).map((_, index) => {
            const isActive = index === selectedIndex;
            const isCompleted = index < selectedIndex;

            return (
              <div key={index} className="h-1 w-10 overflow-hidden rounded-full bg-[#E5E7EB]">
                <div
                  key={`${selectedIndex}-${index}`}
                  className={cn(
                    'h-1 w-full origin-left rounded-full bg-primary',
                    isActive && 'mediatip-hero-progress',
                  )}
                  style={{
                    transform: isCompleted ? 'scaleX(1)' : 'scaleX(0)',
                    animationPlayState: autoplayReady ? 'running' : 'paused',
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className={cn('absolute inset-0 z-0 h-full w-full', 'ea-hero__overlay-background')} />
    </section>
  );
}

const Slide = memo(function Slide({
  slide,
  index,
  onImageSettled,
}: {
  slide: TDynamicData;
  index: number;
  onImageSettled?: () => void;
}) {
  const Tag = 'h2';

  const fields = slide.fields ?? [];
  const subtitle = fields[0]?.value;
  const title = slide.name;
  const description = fields[1]?.value;
  const configuredLink = fields[2]?.value ?? '#';
  const link = HERO_LINK_FALLBACKS[configuredLink] ?? configuredLink;
  const badge = fields[3]?.values?.[0]?.name;
  const imageUrl = slide.image?.url;

  return (
    <div
      className={cn(
        'w-full max-w-[1200px]',
        'grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-12 xl:gap-16',
      )}
    >
      <div className="relative z-10 col-span-1 flex w-full items-center justify-center">
        {!!badge && (
          <div className="absolute -top-[72px] right-[52px] hidden aspect-square w-[154px] items-center justify-center rounded-full bg-success-foreground md:flex">
            <strong className="text-center text-2xl font-bold uppercase leading-[120%] tracking-tight text-white">
              {badge}
            </strong>
          </div>
        )}

        <Image
          src={imageUrl ?? '/images/placeholder.png'}
          alt={resolveImageAlt(undefined, subtitle, title)}
          width={0}
          height={0}
          className="static z-10 h-[230px] w-auto max-w-full object-contain object-center md:absolute md:left-1/2 md:top-1/2 md:h-auto md:max-h-[400px] md:w-full md:-translate-x-1/2 md:-translate-y-1/2"
          sizes="(max-width: 767px) calc(100vw - 32px), (max-width: 1279px) 50vw, 568px"
          priority={index === 0}
          fetchPriority={index === 0 ? 'high' : 'low'}
          loading={index === 0 ? 'eager' : 'lazy'}
          onLoad={onImageSettled}
          onError={onImageSettled}
        />
      </div>
      <div className="relative z-20 col-span-1 flex w-full flex-col items-start justify-start gap-6 lg:gap-8">
        <div className="flex w-full flex-col items-start justify-start gap-3 lg:gap-4">
          <div className="w-full flex-col items-start justify-start gap-2.5">
            {subtitle && <p className="h4 text-primary">{subtitle}</p>}
            <Tag data-ea-visual-tag={index === 0 ? 'h1' : undefined} className={cn('h1', 'text-left font-semibold tracking-tight text-black')}>
              {title}
            </Tag>
          </div>
          <Paragraph className="w-full">{description}</Paragraph>
        </div>
        <Button asChild>
          <Link prefetch={false} prefetchOnHover href={link}>
            <ButtonIcon alt="Kúpiť teraz" url="/images/icons/shopping-bag-add-white.svg" />
            Kúpiť teraz
          </Link>
        </Button>
      </div>
    </div>
  );
});
